import React, { useState, useMemo } from "react";
import ClearButton from "../Buttons/ClearButton";
import ParrotMessage from "../Chat/ParrotMessage";
import ResponseMessage from "../Chat/ResponseMessage";
import { WriteActivity } from "@shared_types";
import { haptic } from "@/lib/haptic";
import WordButton3D from "../Buttons/WordButton3D";
const shuffleOptions = (options: string[]): string[] => {
  return options.sort(() => Math.random() - 0.5);
};

interface WriteProps {
  writeActivity: WriteActivity;
  onActivityFinished: () => void;
  lessonId: number;
}

type State = "start" | "selected";

interface WriteButtonState {
  id: number;
  state: State;
  char: string;
}
interface SelectedOption {
  id: number;
  char: string;
}

const Write: React.FC<WriteProps> = ({
  writeActivity,
  onActivityFinished,
  lessonId,
}) => {
  const answerString = writeActivity.answer.toLowerCase();
  const options = useMemo(
    () => shuffleOptions(wordSplitFunc(answerString)),
    [lessonId]
  );
  const [timesIncorrect, setTimesIncorrect] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>([]);
  const [responseState, setResponseState] = useState<
    "start" | "incorrect" | "correct"
  >("start");
  // we build the button states from looking at which chatarcters area already
  // selected
  const selectButtons: WriteButtonState[] = options.map(
    (char: string, index: number) => {
      // change selected char state
      for (const option of selectedOptions) {
        if (char == option.char && option.id == index) {
          return {
            id: index,
            state: "selected",
            char: char,
          };
        }
      }
      return {
        id: index,
        state: "start",
        char: char,
      };
    }
  );

  const handleClear = () => {
    haptic.soft();
    setResponseState("start");
    setSelectedOptions([]);
  };

  const handleOptionClick = (_char: string, index: number) => {
    const buttonState = selectButtons[index];
    // remove the button clicked from the selected options starting at the back
    if (buttonState.state == "selected") {
      haptic.selection();
      setSelectedOptions((prev) => {
        const toRemoveId = buttonState.id;
        const newSelectedOptions = prev.filter(
          (option) => toRemoveId != option.id
        );
        setResponseState("start");
        // before set state check win
        return newSelectedOptions;
      });
    } else {
      setSelectedOptions((prev) => {
        const newSelectedOption = {
          id: buttonState.id,
          char: buttonState.char,
        };
        const newSelectedOptions = [...prev, newSelectedOption];
        // before set state check win
        const wordInputFinished = newSelectedOptions.length == options.length;
        if (!wordInputFinished) {
          return newSelectedOptions;
        }
        const gotAnswer = newSelectedOptions.reduce((acc, selectedOptions) => {
          return acc + selectedOptions.char;
        }, "");
        if (gotAnswer != answerString.toLowerCase()) {
          // display incorrect feedback
          haptic.error();
          setTimesIncorrect((prev) => prev + 1);
          setResponseState("incorrect");
          // Auto-clear after 1 second
          setTimeout(() => {
            setSelectedOptions([]);
            setResponseState("start");
          }, 1000);
          return newSelectedOptions;
        }
        haptic.success();
        setResponseState("correct");
        onActivityFinished();
        return newSelectedOptions;
      });
    }
  };

  const selectedOptionsText = selectedOptions.reduce((acc, selectedOptions) => {
    return acc + selectedOptions.char;
  }, "");

  const displayClearCls = selectedOptions.length > 0 ? "visible" : "invisible";

  const wrongAwnserCls = "bg-red-600 bg-opacity-80 text-white";
  const correctAnswerCls = "bg-green-600 bg-opacity-80 text-white";
  let responseCls;
  switch (responseState) {
    case "correct":
      responseCls = correctAnswerCls;
      break;
    case "incorrect":
      responseCls = wrongAwnserCls;
      break;
    default:
      responseCls = "";
      break;
  }
  const showHelpTextCls = timesIncorrect >= 2 && "!visible";

  return (
    <div className="appPadding flex flex-col items-center w-full gap-5 px-6">
      <div className="w-full text-xl font-bold pb-8">
        Translate this word and use the buttons to write it out
      </div>
      <ParrotMessage message={writeActivity.question} className="text-lg" />
      <ResponseMessage
        className={`${responseCls} text-lg`}
        message={selectedOptionsText}
      />
      <div
        className={`text-[var(--color-orange)] invisible ${showHelpTextCls}`}
      >
        pstt... it's {answerString}
      </div>
      <div className="w-full flex flex-wrap gap-1 justify-center">
        {selectButtons.map((buttonState) => (
          <WordButton3D
            key={buttonState.id}
            word={buttonState.char}
            index={buttonState.id}
            onClick={handleOptionClick}
            disabled={buttonState.state === "selected"}
          />
        ))}
      </div>
      <div className={"flex gap-4 " + displayClearCls}>
        <ClearButton onClick={handleClear} />
      </div>
    </div>
  );
};

// SPLIT a spanish word into its syllables
// if not splitting correctly maybe we are missing a consonant cluster
// CODE from gpt o1 model. dont trust too much. Seems to work ok
function wordSplitFunc(word: string): string[] {
  // Normalize the word
  word = word.toLowerCase();
  const letters = word.split("");
  if (letters.length <= 4) {
    return letters;
  }
  // Helper functions
  const VOWELS = [
    "a",
    "e",
    "i",
    "o",
    "u",
    "á",
    "é",
    "í",
    "ó",
    "ú",
    "ü",
    "ï",
    "ö",
  ];
  const STRONG_VOWELS = ["a", "e", "o", "á", "é", "ó"];
  const WEAK_VOWELS = ["i", "u", "ü", "í", "ú"];

  function isVowel(char: string): boolean {
    return VOWELS.includes(char);
  }

  function isConsonant(char: string): boolean {
    return !isVowel(char);
  }

  function isDiphthong(a: string, b: string): boolean {
    // A diphthong occurs when a strong vowel and a weak vowel or two weak vowels are together
    if (VOWELS.includes(a) && VOWELS.includes(b)) {
      if (
        (WEAK_VOWELS.includes(a) && WEAK_VOWELS.includes(b)) ||
        (WEAK_VOWELS.includes(a) && STRONG_VOWELS.includes(b)) ||
        (STRONG_VOWELS.includes(a) && WEAK_VOWELS.includes(b))
      ) {
        // Accented weak vowel breaks the diphthong
        if (a === "í" || a === "ú" || b === "í" || b === "ú") {
          return false;
        }
        return true;
      }
    }
    return false;
  }

  // Consonant clusters that can start a syllable
  const consonantClusters = [
    "bl",
    "br",
    "ch",
    "cl",
    "cr",
    "dr",
    "fl",
    "fr",
    "gl",
    "gr",
    "pl",
    "pr",
    "tr",
    "ll",
    "rr",
  ];

  const syllables: string[] = [];
  let idx = 0;

  while (idx < word.length) {
    const syllableStart = idx;

    // Step 1: Onset consonants
    while (idx < word.length && isConsonant(word[idx])) {
      if (
        idx + 1 < word.length &&
        consonantClusters.includes(word.slice(idx, idx + 2))
      ) {
        // Consonant cluster that can start a syllable
        idx += 2;
      } else {
        idx += 1;
      }
      // If consonant is at the end of the word, break
      if (idx >= word.length || isVowel(word[idx])) {
        break;
      }
    }

    // Step 2: Nucleus vowels
    if (idx < word.length && isVowel(word[idx])) {
      idx += 1;
      // Check for diphthongs
      if (idx < word.length && isVowel(word[idx])) {
        if (isDiphthong(word[idx - 1], word[idx])) {
          idx += 1;
        }
      }
    }

    // Step 3: Handle consonant(s) after vowel nucleus
    const consonantSeqStart = idx;
    while (idx < word.length && isConsonant(word[idx])) {
      idx += 1;
    }
    const consonantSeq = word.slice(consonantSeqStart, idx);

    // Spanish syllabification rules for consonant sequences
    if (consonantSeq.length > 0 && idx < word.length) {
      if (consonantSeq.length === 1) {
        // Single consonant between vowels goes with the next syllable
        idx = consonantSeqStart;
      } else if (consonantSeq.length === 2) {
        if (consonantClusters.includes(consonantSeq)) {
          // Consonant cluster that can start a syllable stays with the next syllable
          idx = consonantSeqStart;
        } else {
          // Split between consonants
          idx = consonantSeqStart + 1;
        }
      } else if (consonantSeq.length === 3) {
        // Split after first consonant
        idx = consonantSeqStart + 1;
      }
    }

    syllables.push(word.slice(syllableStart, idx));
  }

  return syllables;
}

export default Write;
